import { type JSX } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'

import { Body1, Title1 } from '@ui/Typography'
import { Button } from '@ui/components/Button'

import { isSumsubSupported } from '@utils/AccountAccess/accountKYCStatuses'
import { useWindowResize } from '@utils/domUtils'
import { CommonPaths } from '@utils/path'

import { DocumentCategoryLinkMap, DocumentCategoryType } from '@model/DocumentCategories'
import { NameDto } from '@model/NameDto'

import { DocumentManagementCategoryModal } from './modals/DocumentManagementCategory.modal'
import { AdditionalDocument, useAdditionalDocuments } from './useAdditionalDocuments'

import styles from './DocumentManagementPage.module.scss'

export const UploadAdditionalDocuments = (): JSX.Element => {
  const navigate = useNavigate()
  const { account } = useAccountReadContext()

  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const docData = useAdditionalDocuments()
  const categoryModal = useModal(DocumentManagementCategoryModal)

  const handleSelectDoc = (
    documentCategory: NameDto<DocumentCategoryType>,
    documentCategoryType: NameDto
  ) => {
    navigate(`${CommonPaths.DocumentManagement}/${DocumentCategoryLinkMap[documentCategory.id]}`, {
      state: {
        documentCategory,
        documentCategoryType,
      },
    })
  }

  const handleDocUpload = (doc: AdditionalDocument) => {
    if (doc.category === DocumentCategoryType.Personal) {
      if (isSumsubSupported(account)) {
        navigate(CommonPaths.Sumsub)
        return
      }
    }

    return categoryModal.open({
      documentCategoryType: doc.category,
      onSelectOption: handleSelectDoc,
    })
  }

  return (
    <>
      {docData.map((doc) =>
        doc ? (
          <div key={doc.title} className={styles.additionalCard}>
            <div className={styles.textBox}>
              <Title1 medium>{doc.title}</Title1>
              <div className={styles.descriptionBox}>
                {doc.text && <Body1 className={styles.description}>{doc.text}</Body1>}
                {doc.instructions && (
                  <Body1 className={styles.instructions}>{doc.instructions}</Body1>
                )}
              </div>
            </div>
            <Button.Primary size={isMobile ? 'lg' : 'md'} onClick={() => handleDocUpload(doc)}>
              {t('Profile.Upload')}
            </Button.Primary>
          </div>
        ) : (
          <></>
        )
      )}
    </>
  )
}
